import HavokPhysics from '@babylonjs/havok';

import CoreUtils from '../../utils/coreUtils.js';
import Utils3D from '../utils/utils3D.js';
import Group from './group.js';
import Subnet from './subnet.js';
import Sticky from './sticky.js';
import { ASSET_TYPE } from './assetCacher.js';
import { ENABLE_CACHING, setEnableCaching } from './constants.js';
import fillMissingBJSFunctions from './babylonPolyfill.js';

import NodeExtrude from './nodes/nodeExtrude.js';
import NodeExtrudeV2 from './nodes/nodeExtrudeV2.js';
import NodeLatticeDeform from './nodes/nodeLatticeDeform.js';
import NodeTaper from './nodes/nodeTaper.js';
import NodeTransformV2 from './nodes/nodeTransformV2.js';
import NodeNumber from './nodes/nodeNumber.js';
import NodeSubnetInputs from './nodes/nodeSubnetInputs.js';
import NodeMerge from './nodes/nodeMerge.js';
import NodeExpression from './nodes/nodeExpression.js';
import NodeInterpolate from './nodes/nodeInterpolate.js';
import NodeCreateBox from './nodes/nodeCreateBox.js';
//import NodeCreateTree from './nodes/nodeCreateTree.js';
import NodeCreateCylinder from './nodes/nodeCreateCylinder.js';
import NodeCreateSphere from './nodes/nodeCreateSphere.js';
import NodeCreateLine from './nodes/nodeCreateLine.js';
import NodeCreateRoof from './nodes/nodeCreateRoof.js';
import NodeConvexHull from './nodes/nodeConvexHull.js';
import NodeArrayGeometry from './nodes/nodeArrayGeometry.js';
import NodeDuplicate from './nodes/nodeDuplicate.js';
import NodeMaterial from './nodes/nodeMaterial.js';
import NodeLoadMaterial from './nodes/nodeLoadMaterial.js';
import NodeFile from './nodes/nodeFile.js';
import NodeNull from './nodes/nodeNull.js';
import NodeMorph from './nodes/nodeMorph.js';
import NodeClearAssetAttributes from  './nodes/nodeClearAssetAttributes.js';
import NodeProceduralAsset from './nodes/nodeProceduralAsset.js';
import NodeBoolean from './nodes/nodeBoolean.js';
import NodeAlign from './nodes/nodeAlign.js';
import NodeFit from './nodes/nodeFit.js';
import NodeDistribute from './nodes/nodeDistribute.js';
import NodeSetPivot from './nodes/nodeSetPivot.js';
import NodeCreateCurve from './nodes/nodeCreateCurve.js';
import NodeGroupObjects from './nodes/nodeGroupObjects.js';
import NodeCombineMeshes from './nodes/nodeCombineMeshes.js';
import NodeLoadCurve from './nodes/nodeLoadCurve.js';
import NodeSwitch from './nodes/nodeSwitch.js';
import NodeComputeBBox from './nodes/nodeComputeBBox.js';
import NodeMaterialTweaks from './nodes/nodeMaterialTweaks.js';
import NodeMirror from './nodes/nodeMirror.js';
import NodeMirrorV2 from './nodes/nodeMirrorV2.js';
import NodeApplyUVs from './nodes/nodeApplyUVs.js';
import NodeRandomizeUVs from './nodes/nodeRandomizeUVs.js';
import NodeTransformUVs from './nodes/nodeTransformUVs.js';
import NodeCopyAlongPath from './nodes/nodeCopyAlongPath.js';
import NodeCopyAlongPathV2 from './nodes/nodeCopyAlongPathV2.js';
import NodeCreateRectangle from './nodes/nodeCreateRectangle.js';
import NodeSmoothNormals from './nodes/nodeSmoothNormals.js';
import NodeCreateSpiral from './nodes/nodeCreateSpiral.js';
import NodeCreateArc from './nodes/nodeCreateArc.js';
import NodeResampleCurve from './nodes/nodeResampleCurve.js';
import NodeSimplifyCurve from './nodes/nodeSimplifyCurve.js';
import NodeConvertCurve from './nodes/nodeConvertCurve.js';
import NodeBend from './nodes/nodeBend.js';
import NodeSubdivideMesh from './nodes/nodeSubdivideMesh.js';
import NodeJoinCurves from './nodes/nodeJoinCurves.js';
import NodeOffsetCurve from './nodes/nodeOffsetCurve.js';
import NodeThickenCurve from './nodes/nodeThickenCurve.js';
import NodeLathe from './nodes/nodeLathe.js';
import NodeLoft from './nodes/nodeLoft.js';
import NodeDistributeOnSurface from './nodes/nodeDistributeOnSurface.js';
import NodeSurfaceMould from './nodes/nodeSurfaceMould.js';
import NodeDelete from './nodes/nodeDelete.js';
import NodeDeleteByVolume from './nodes/nodeDeleteByVolume.js';

const NODE_CATEGORIES = {
	GEO:		0,
	CURVES:		5,
	TRANSFORM:	10,
	MATH:		20,
	CONTAINERS:	30,
	GROUP:		40,
	MATERIAL:	50,
	MISC:		60,
};

export const NODE_CATEGORY_LABELS = {
	[NODE_CATEGORIES.GEO]:			'Geometry',
	[NODE_CATEGORIES.CURVES]:		'Curves',
	[NODE_CATEGORIES.TRANSFORM]:	'Transforms',
	[NODE_CATEGORIES.MATH]:			'Math',
	[NODE_CATEGORIES.GROUP]:		'Merge',
	[NODE_CATEGORIES.CONTAINERS]:	'Organize',
	[NODE_CATEGORIES.MATERIAL]:		'Material',
	[NODE_CATEGORIES.MISC]:			'Miscellaneous',
};

let gNodeEngine = null;

export default class NodeEngine
{
	static logDetailedGenerationTime = false;

	constructor()
	{
		this.nodeTypes = {};
		this.nodeCategories = {};

		this.enableRegisteringGraphs = true;

		this.registerTypes();

		fillMissingBJSFunctions();

		HavokPhysics().then((havok) => {
			// Havok is now available
			window.HK = havok;
		});

		this.graphs = {};

		// This is an optimization to combine meshes - for imported ones such as handles, etc.
		// we can combine them for display in real time, but not for export. This flag controls that.
		this.enableCombiningImportedMeshes = true;

		if(window.featureFlags && !window.featureFlags['enableProceduralFixtureCaching'])
			setEnableCaching(false);
		CoreUtils.log(`Node engine caching optimizations ${ENABLE_CACHING ? 'enabled' : 'disabled'}`);
	}

	static getInstance()
	{
		if(!gNodeEngine)
			gNodeEngine = new NodeEngine();
		return gNodeEngine;
	}

	registerTypes()
	{
		this.registerNodeType('number', NODE_CATEGORIES.MATH, (pParent) => { return new NodeNumber(pParent); });
		this.registerNodeType('expression', NODE_CATEGORIES.MATH, (pParent) => { return new NodeExpression(pParent); });
		this.registerNodeType('interpolate', NODE_CATEGORIES.MATH, (pParent) => { return new NodeInterpolate(pParent); });
		
		this.registerNodeType('box', NODE_CATEGORIES.GEO, (pParent) => { return new NodeCreateBox(pParent); });
		this.registerNodeType('sphere', NODE_CATEGORIES.GEO, (pParent) => { return new NodeCreateSphere(pParent); });
		this.registerNodeType('cylinder', NODE_CATEGORIES.GEO, (pParent) => { return new NodeCreateCylinder(pParent); });
		this.registerNodeType('file', NODE_CATEGORIES.GEO, (pParent) => { return new NodeFile(pParent); });		
		this.registerNodeType('boolean', NODE_CATEGORIES.GEO, (pParent) => { return new NodeBoolean(pParent); });
		this.registerNodeType('extrude', NODE_CATEGORIES.GEO, (pParent) => { return new NodeExtrude(pParent); }, true);
		this.registerNodeType('extrude_v2', NODE_CATEGORIES.GEO, (pParent) => { return new NodeExtrudeV2(pParent); }, false, 'Extrude');
		this.registerNodeType('lathe', NODE_CATEGORIES.GEO, (pParent) => { return new NodeLathe(pParent); });
		this.registerNodeType('loft', NODE_CATEGORIES.GEO, (pParent) => { return new NodeLoft(pParent); });
		//this.registerNodeType('createRoof', NODE_CATEGORIES.GEO, (pParent) => { return new NodeCreateRoof(pParent); });
		this.registerNodeType('surfaceMould', NODE_CATEGORIES.GEO, (pParent) => { return new NodeSurfaceMould(pParent); }, true);
		this.registerNodeType('convexHull', NODE_CATEGORIES.GEO, (pParent) => { return new NodeConvexHull(pParent); });
// TEMP
//this.registerNodeType('tree', NODE_CATEGORIES.GEO, (pParent) => { return new NodeCreateTree(pParent); });
		this.registerNodeType('proceduralAsset', NODE_CATEGORIES.GEO, (pParent) => { return new NodeProceduralAsset(pParent); });	

		//this.registerNodeType('extractPath', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeExtractPath(pParent); });
		this.registerNodeType('createCurve', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeCreateCurve(pParent); });
		this.registerNodeType('loadCurve', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeLoadCurve(pParent); });
		this.registerNodeType('createRectangle', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeCreateRectangle(pParent); });
		this.registerNodeType('createSpiral', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeCreateSpiral(pParent); });
		this.registerNodeType('createArc', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeCreateArc(pParent); });
		this.registerNodeType('createLine', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeCreateLine(pParent); });
		this.registerNodeType('resampleCurve', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeResampleCurve(pParent); });
		this.registerNodeType('joinCurves', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeJoinCurves(pParent); });
		this.registerNodeType('offsetCurve', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeOffsetCurve(pParent); });		
		this.registerNodeType('thickenCurve', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeThickenCurve(pParent); });		
		this.registerNodeType('convertCurve', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeConvertCurve(pParent); });
		this.registerNodeType('simplifyCurve', NODE_CATEGORIES.CURVES, (pParent) => { return new NodeSimplifyCurve(pParent); });

		// DEPRECATED: this.registerNodeType('transform', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeTransform(pParent); }, true);
		this.registerNodeType('transform_v2', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeTransformV2(pParent); }, false, 'Transform');
		this.registerNodeType('align', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeAlign(pParent); });
		this.registerNodeType('fit', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeFit(pParent); });
		this.registerNodeType('distribute', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeDistribute(pParent); });
		this.registerNodeType('distributeOnSurface', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeDistributeOnSurface(pParent); });
		this.registerNodeType('mirror', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeMirror(pParent); }, true);
		this.registerNodeType('mirror_v2', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeMirrorV2(pParent); }, false, 'Mirror');
		this.registerNodeType('array', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeArrayGeometry(pParent); });		
		this.registerNodeType('duplicate', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeDuplicate(pParent); });
		this.registerNodeType('bend', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeBend(pParent); });
		this.registerNodeType('latticeDeform', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeLatticeDeform(pParent); });
		this.registerNodeType('taper', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeTaper(pParent); });
		this.registerNodeType('copyAlongPath', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeCopyAlongPath(pParent); }, true);
		this.registerNodeType('copyAlongPath_v2', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeCopyAlongPathV2(pParent); }, false, 'CopyAlongPath');
		this.registerNodeType('morph', NODE_CATEGORIES.TRANSFORM, (pParent) => { return new NodeMorph(pParent); });

		this.registerNodeType('loadMaterial', NODE_CATEGORIES.MATERIAL, (pParent) => { return new NodeLoadMaterial(pParent); });
		this.registerNodeType('applyUVs', NODE_CATEGORIES.MATERIAL, (pParent) => { return new NodeApplyUVs(pParent); });
		this.registerNodeType('randomizeUVs', NODE_CATEGORIES.MATERIAL, (pParent) => { return new NodeRandomizeUVs(pParent); });
		this.registerNodeType('transformUVs', NODE_CATEGORIES.MATERIAL, (pParent) => { return new NodeTransformUVs(pParent); });
		this.registerNodeType('materialTweaks', NODE_CATEGORIES.MATERIAL, (pParent) => { return new NodeMaterialTweaks(pParent); });
		this.registerNodeType('material', NODE_CATEGORIES.MATERIAL, (pParent) => { return new NodeMaterial(pParent); });

		this.registerNodeType('merge', NODE_CATEGORIES.GROUP, (pParent) => { return new NodeMerge(pParent); });
		this.registerNodeType('combineMeshes', NODE_CATEGORIES.GROUP, (pParent) => { return new NodeCombineMeshes(pParent); });
		this.registerNodeType('groupObjects', NODE_CATEGORIES.GROUP, (pParent) => { return new NodeGroupObjects(pParent); }, true);

		this.registerNodeType('group', NODE_CATEGORIES.CONTAINERS, (pParent) => { return new Group(pParent); });
		this.registerNodeType('subnet', NODE_CATEGORIES.CONTAINERS, (pParent) => { return new Subnet(pParent.getGraph(), pParent); });
		this.registerNodeType('subnetInputs', NODE_CATEGORIES.CONTAINERS, (pParent) => { return new NodeSubnetInputs(pParent); }, true);

		this.registerNodeType('setPivot', NODE_CATEGORIES.MISC, (pParent) => { return new NodeSetPivot(pParent); });
		this.registerNodeType('switch', NODE_CATEGORIES.MISC, (pParent) => { return new NodeSwitch(pParent); });
		this.registerNodeType('computeBBox', NODE_CATEGORIES.MISC, (pParent) => { return new NodeComputeBBox(pParent); });
		this.registerNodeType('smoothNormals', NODE_CATEGORIES.MISC, (pParent) => { return new NodeSmoothNormals(pParent); });
		this.registerNodeType('subdivideMesh', NODE_CATEGORIES.MISC, (pParent) => { return new NodeSubdivideMesh(pParent); });
		this.registerNodeType('delete', NODE_CATEGORIES.MISC, (pParent) => { return new NodeDelete(pParent); });
		this.registerNodeType('deleteByVolume', NODE_CATEGORIES.MISC, (pParent) => { return new NodeDeleteByVolume(pParent); });
		this.registerNodeType('sticky', NODE_CATEGORIES.MISC, (pParent) => { return new Sticky(pParent); });
		this.registerNodeType('null', NODE_CATEGORIES.MISC, (pParent) => { return new NodeNull(pParent); });
		this.registerNodeType('clearAssetAttributes', NODE_CATEGORIES.MISC, (pParent) => { return new NodeClearAssetAttributes(pParent); });
	}

	registerNodeType(strType, categoryType, funcGenerator, bIsHiddenFromUI = false, strOptLabel = null)
	{
		this.nodeTypes[strType] = { 
			type: strType, 
			generator: funcGenerator, 
			isHidden: bIsHiddenFromUI, 
			category: categoryType, 
			label: strOptLabel ? strOptLabel : strType
		};
		if(!this.nodeCategories[categoryType])
			this.nodeCategories[categoryType] = [];
		this.nodeCategories[categoryType].push(this.nodeTypes[strType]);
	}

	getNodeTypeInfo(strNodeType)
	{
		// See if the node has been removed and has an updated version:
		let strFinalType = strNodeType;
		if(!this.nodeTypes[strFinalType])
		{
			let strCurrType = strNodeType;
			do
			{
				strFinalType = strCurrType;
				strCurrType = CoreUtils.incrementNumericSuffix(strCurrType, '_v');
			}
			while(this.nodeTypes[strCurrType]);
		}

		if(strFinalType !== strNodeType)
			console.warn(`Automatically updating node type from ${strNodeType} to ${strFinalType}`);

		if(!this.nodeTypes[strFinalType])
			throw new Error(`Critical error: could not find the definition for node type ${strNodeType}`);

		return this.nodeTypes[strFinalType];
	}

	getNodeCategories()
	{
		return this.nodeCategories;
	}

	getNodeTypes()
	{
		return this.nodeTypes;
	}

	registerGraph(pGraph)
	{
		if(this.enableRegisteringGraphs)
			this.graphs[pGraph.getGraphId()] = pGraph;
	}

	unregisterGraph(pGraph)
	{
		if(this.enableRegisteringGraphs)
			delete this.graphs[pGraph.getGraphId()];
	}

	getGraph(graphId)
	{
		return this.graphs[graphId];
	}

	setIsGraphRegistryEnabled(bValue)
	{
		this.enableRegisteringGraphs = bValue;
	}

	getNodeForMeshAt(fX, fY, scene, assetType = null)
	{
		let pickedMesh = Utils3D.pickMesh(fX, fY, scene, (mesh) =>
		{
			return mesh.parentGraphId !== undefined;
		});

		return this.getNodeForMesh(pickedMesh, assetType);
	}

    getNodeForMesh(mesh, assetType = null)
	{
        if(!mesh)
            return null;

        const graph = this.graphs[mesh.parentGraphId];
        if(!graph)
            return null;

        if(!assetType)
            return graph.getNode(mesh.parentNodeId);

        switch(assetType)
		{
			case ASSET_TYPE.MATERIAL:
				return graph.getNode(mesh.parentMaterialNodeId);
			default:
				return graph.getNode(mesh.parentNodeId);
        }
    }

	getEnableCombiningImportedMeshes()
	{
		return this.enableCombiningImportedMeshes;
	}

	setEnableCombiningImportedMeshes(bValue)
	{
		this.enableCombiningImportedMeshes = bValue;
		for(let graphId in this.graphs)
			this.graphs[graphId].getNodesOfType('combineMeshes').forEach((node) => { node.markDirty(); });
	}
}